import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { Alert, Row } from 'reactstrap';
import { defineMessages, FormattedMessage } from 'react-intl';
import { uniqueId } from 'lodash';

import { modelOf } from '../../../prop-types';
import globalTranslations from '../../../i18n/globalTranslations';
import CustomCol from '../../bootstrap/CustomCol';
import Icon from '../../common/Icon';
import OrderTotalClass from '../../../types/OrderTotalClass';
import Price from '../../product/Price';
import ConfigStore from '../../../store/ConfigStore';
import OrderStore from '../../../store/OrderStore';

const messages = defineMessages({
  orderCompleted: {
    id: 'checkout.orderCompleted',
    defaultMessage: 'Your order has been received.'
  },
  paymentInstructions: {
    id: 'checkout.paymentInformation',
    defaultMessage:
      'Confirmation email has also been sent to the email address {email}. Make sure email address is correct.'
  },
  paymentInstructions2: {
    id: 'checkout.paymentInformation2',
    defaultMessage:
      "If you don't see the message, be sure to check your spam folder as well."
  },
  shoppingCenterMerchantInfo: {
    id: 'checkout.shoppingCenterMerchantInfo',
    defaultMessage: "merchant's {merchant}"
  },
  trackOrder: {
    id: 'checkout.trackOrder',
    defaultMessage:
      'You can track your order by clicking {link}, you can also get to the {merchantInfo} order tracking page from the confirmation email.'
  },
  orderInformationTitle: {
    id: 'checkout.orderInformationTitle',
    defaultMessage: 'Order information'
  },
  orderConfirmationThankYou: {
    id: 'checkout.orderConfirmationThankYou',
    defaultMessage: 'Thank you for your order!'
  }
});

@observer
export class CheckoutOrderConfirmation extends Component {
  renderSuccessPage = () => {
    const { orderStore } = this.props;
    const { currentOrder } = orderStore;

    if (!currentOrder) {
      return null;
    }

    return (
      <CustomCol xs={12} md={10} xxl={7} className="CheckoutOrderConfirmation">
        {this.renderOrderConfirmation()}
      </CustomCol>
    );
  };

  renderOrderConfirmation = () => {
    const { configStore, orderStore } = this.props;
    const { currentOrder } = orderStore;
    return (
      <Fragment>
        {this.renderOrderSuccessInformation()}
        {configStore.account.enableMyAccount &&
          this.renderOrderTrackingInformation()}
        {currentOrder.has_licensed_products &&
          this.renderAlert(
            'warning',
            <FormattedMessage
              id="checkout.successfulPurchaseLicensedProductsSentence"
              defaultMessage="Some products in your order require a license. You may pay your order now; after this, you are required to send the relevant licenses to us by email. Please refer to the shipping terms page for more details."
            />
          )}
        {this.renderOrderInformation()}
      </Fragment>
    );
  };

  renderOrderSuccessInformation = () => {
    const { orderStore } = this.props;
    const { currentOrder } = orderStore;

    const email = currentOrder.confirmation_email;

    return (
      <Fragment>
        {this.renderContentRow({ xs: 12 }, <Icon name="check" />, {
          className: 'CheckoutOrderConfirmation__order-success-icon'
        })}
        {this.renderContentRow(
          { xs: 12 },
          <FormattedMessage {...messages.orderCompleted} />,
          {
            className: 'CheckoutOrderConfirmation__order-completed'
          }
        )}
        {email &&
          this.renderContentRow(
            { xs: 12 },
            <FormattedMessage
              {...messages.paymentInstructions}
              values={{
                email
              }}
            />,
            {
              className: 'CheckoutOrderConfirmation__payment-instructions'
            }
          )}
        {email &&
          this.renderContentRow(
            { xs: 12 },
            <FormattedMessage {...messages.paymentInstructions2} />,
            {
              className: 'CheckoutOrderConfirmation__payment-instructions'
            }
          )}
      </Fragment>
    );
  };

  renderOrderTrackingInformation = () => {
    const { orderStore } = this.props;
    const { currentOrder } = orderStore;

    const paymentMethodMessages = currentOrder.payment_method_messages;
    const translationValues = this.getTranslationValues();

    return this.renderContentRow(
      { xs: 12 },
      <div className="CheckoutOrderConfirmation__order-confirmation-message-track-order">
        {currentOrder.tracking_url && (
          <FormattedMessage
            {...messages.trackOrder}
            values={translationValues}
          />
        )}
        <div className="CheckoutOrderConfirmation__payment-method-messages">
          {paymentMethodMessages &&
            paymentMethodMessages.length > 0 &&
            paymentMethodMessages.map((message, index) =>
              this.renderAlert('warning', <span>{message}</span>, {
                key: index
              })
            )}
        </div>
      </div>,
      {
        className: 'CheckoutOrderConfirmation__order-confirmation-message'
      }
    );
  };

  getTranslationValues = () => {
    const { orderStore, configStore } = this.props;
    const { currentOrder } = orderStore;
    const merchant = (
      <FormattedMessage
        {...messages.shoppingCenterMerchantInfo}
        values={{
          merchant: currentOrder.merchant_name
        }}
      />
    );

    const translationValues = {
      link: (
        <a href={currentOrder.tracking_url} target="_blank" rel="noopener">
          <FormattedMessage {...globalTranslations.hereSentence} />
        </a>
      )
    };

    translationValues.merchantInfo = configStore.siteConfig.isShoppingCenter
      ? merchant
      : '';

    return translationValues;
  };

  renderOrderInformation = () => (
    <Fragment>
      {this.renderContentRow(
        { xs: 12 },
        <FormattedMessage {...messages.orderInformationTitle} />,
        {
          className: 'CheckoutOrderConfirmation__order-information-title'
        }
      )}
      {this.renderContentRow({ xs: 12 }, this.renderOrderInformationContent())}
      {this.renderContentRow(
        { xs: 12 },
        <div className="CheckoutOrderConfirmation__order-information-thankyou">
          <FormattedMessage {...messages.orderConfirmationThankYou} />
        </div>
      )}
    </Fragment>
  );

  renderOrderInformationContent = () => {
    const { orderStore } = this.props;
    const { currentOrder } = orderStore;

    const { paymentMethod } = currentOrder;
    const { shippingMethod } = currentOrder;
    const id = currentOrder.id;
    const paymentMethodName = paymentMethod.name;
    const shippingMethodName = shippingMethod.name;
    const pickupPoint = currentOrder.pickupPoint;

    return (
      <div className="CheckoutOrderConfirmation__order-information-content">
        {this.renderContentRow(
          { xs: 12 },
          <div className="CheckoutOrderConfirmation__order-information-product-wrapper">
            {currentOrder.products.map((product) => (
              <div
                key={product.id}
                className="CheckoutOrderConfirmation__order-information-product"
              >
                <span>
                  {product.name} x {product.quantity}
                </span>
                <span className="CheckoutOrderConfirmation__order-information-value">
                  <Price price={product.final_unit_price} />
                </span>
              </div>
            ))}
          </div>
        )}
        {this.renderContentRow(
          { xs: 12 },
          <div className="CheckoutOrderConfirmation__order-information-total-wrapper">
            {currentOrder.totals.map(
              (total) =>
                total.class !== OrderTotalClass.EXTRA_TOTAL && (
                  <div
                    key={uniqueId(total.name)}
                    className="CheckoutOrderConfirmation__order-information-total"
                  >
                    <span>{total.name}</span>
                    <span
                      className="CheckoutOrderConfirmation__order-information-value"
                      dangerouslySetInnerHTML={{
                        __html: total.text
                      }}
                    />
                  </div>
                )
            )}
          </div>
        )}
        {this.renderContentRow(
          { xs: 12 },
          <div className="CheckoutOrderConfirmation__order-information-id">
            <FormattedMessage {...globalTranslations.orderNumber} />
            <span className="CheckoutOrderConfirmation__order-information-value">
              {id}
            </span>
          </div>
        )}
        {this.renderContentRow(
          { xs: 12 },
          <div className="CheckoutOrderConfirmation__order-information-payment-method">
            <FormattedMessage {...globalTranslations.paymentMethod} />
            <span className="CheckoutOrderConfirmation__order-information-value">
              {paymentMethodName}
            </span>
          </div>
        )}
        {this.renderContentRow(
          { xs: 12 },
          <div className="CheckoutOrderConfirmation__order-information-shipping-method">
            <FormattedMessage {...globalTranslations.shippingMethod} />
            <span className="CheckoutOrderConfirmation__order-information-value">
              {shippingMethodName}
            </span>
          </div>
        )}
        {pickupPoint &&
          this.renderContentRow(
            { xs: 12 },
            <div className="CheckoutOrderConfirmation__order-information-pickup-point">
              <FormattedMessage {...globalTranslations.pickupPointTitle} />
              <div>
                <div className="CheckoutOrderConfirmation__order-information-value">
                  {pickupPoint.name}
                </div>
                <div className="CheckoutOrderConfirmation__order-information-value">
                  {pickupPoint.address}
                </div>
              </div>
            </div>
          )}
      </div>
    );
  };

  renderContentRow = (sizes, content, className, rest) => (
    <Row {...rest} {...className}>
      <CustomCol {...sizes}>{content}</CustomCol>
    </Row>
  );

  renderAlert = (type, message, rest) => (
    <Alert {...rest} color={type}>
      {message}
    </Alert>
  );

  render() {
    return <Fragment>{this.renderSuccessPage()}</Fragment>;
  }
}

CheckoutOrderConfirmation.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  orderStore: modelOf(OrderStore).isRequired
};

export default inject('configStore', 'orderStore')(CheckoutOrderConfirmation);



// WEBPACK FOOTER //
// ./src/components/checkout/CheckoutOrderConfirmation/index.jsx